import { useMutation, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { uuidv7 } from "@luckymenu/core";
import { useCallback, useMemo, useRef, useState } from "react";
import { type PaymentClaim, paymentClaims } from "../billing/claims";
import { type BillingError, billingProblemOf } from "../billing/problems";
import { useLiveBoard } from "./use-live-board";
import { useCan } from "./use-navigation";

/**
 * What Confirm did: recorded the UPI payment, or the screen to open because
 * it needs a person: the order's bill screen (`bill`, not billed yet) or
 * its payment screen (`payment`, the amount differs from what is due).
 */
export type ClaimOutcome = "recorded" | "bill" | "payment" | null;

/**
 * "Table 4 says they paid ₹540 by UPI" (P4-T22): the claims from tables'
 * "I have paid", for roles that record payments. A claim stays until a
 * payment is recorded on its bill, on any device. `confirm(claim)`, once
 * staff saw the money arrive, records a UPI payment of the claimed amount
 * when it is exactly what the bill has left (one request ID per claim, so
 * a retry records once); otherwise it answers which screen to open.
 * `hide(claim)` takes it off this device's banner (a new claim shows again).
 */
export function usePaymentClaims() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const canRecord = useCan("payment.record");
	const { board, status } = useLiveBoard();
	const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
	const [busy, setBusy] = useState<string | null>(null);
	const [problem, setProblem] = useState<(BillingError & { key: string }) | null>(null);
	const requestIds = useRef(new Map<string, string>());
	const record = useMutation(trpc.staff.billing.recordPayment.mutationOptions());

	const claims: PaymentClaim[] = useMemo(
		() => (canRecord && status === "ready" ? paymentClaims(board, hidden) : []),
		[canRecord, status, board, hidden],
	);

	const confirm = useCallback(
		async (claim: PaymentClaim): Promise<ClaimOutcome> => {
			if (claim.status === "open") return "bill";
			setProblem(null);
			setBusy(claim.key);
			try {
				const bill = await queryClient.fetchQuery({
					...trpc.staff.billing.getBill.queryOptions({ orderId: claim.orderId }),
					staleTime: 0,
				});
				if (bill.balancePaise !== claim.amountPaise) return "payment";
				const id = requestIds.current.get(claim.key) ?? uuidv7();
				requestIds.current.set(claim.key, id);
				const next = await record.mutateAsync({
					id,
					billId: bill.id,
					method: "upi",
					amountPaise: claim.amountPaise,
				});
				queryClient.setQueryData(
					trpc.staff.billing.getBill.queryKey({ orderId: next.orderId }),
					next,
				);
				queryClient.setQueryData(trpc.staff.billing.getBill.queryKey({ billId: next.id }), next);
				void queryClient.invalidateQueries({ queryKey: trpc.staff.ordering.pathKey() });
				return "recorded";
			} catch (error) {
				setProblem({ ...billingProblemOf(error), key: claim.key });
				return null;
			} finally {
				setBusy(null);
			}
		},
		[queryClient, trpc, record],
	);

	return {
		claims,
		confirm,
		hide: useCallback((claim: PaymentClaim) => setHidden((h) => new Set([...h, claim.key])), []),
		/** The claim being confirmed. */
		busy,
		problem,
	};
}
